import { Injectable } from '@angular/core';
import Timer from 'easytimer.js';

export interface TimerTick {
  /** Valor formatado HH:mm:ss */
  formatted: string;
  /** Total de segundos acumulados */
  totalSeconds: number;
}

export interface TimerCallbacks {
  /** Chamado a cada segundo do timer de progresso */
  onProgressTick: (tick: TimerTick) => void;
  /** Chamado a cada segundo do timer regressivo */
  onRegressivoTick: (tick: TimerTick) => void;
  /** Chamado quando o regressivo chega a zero */
  onRegressivoEnd: () => void;
  /** Chamado a cada segundo do timer de pausa */
  onPausaTick: (tick: TimerTick) => void;
}

@Injectable()
export class ChronometerTimerService {

  private timer = new Timer();
  private timerRegressivo = new Timer();
  private timerParado = new Timer();

  // ─── API Pública ──────────────────────────────────────────────────────────────

  /**
   * Inicia os timers de progresso e regressivo.
   * @param tempoInicial       Segundos acumulados de estudo (para retomada)
   * @param tempoRegressivo    Segundos restantes no regressivo
   * @param plannedSeconds     Total de segundos planejados para o subject
   * @param callbacks          Funções chamadas a cada tick
   */
  startProgress(
    tempoInicial: number,
    tempoRegressivo: number,
    plannedSeconds: number,
    callbacks: Pick<TimerCallbacks, 'onProgressTick' | 'onRegressivoTick' | 'onRegressivoEnd'>
  ): void {
    this.timer.removeAllEventListeners();
    this.timer.addEventListener('secondsUpdated', () => {
      const vals = this.timer.getTimeValues();
      callbacks.onProgressTick({
        formatted: vals.toString(),
        totalSeconds: vals.seconds + vals.minutes * 60 + vals.hours * 3600
      });
    });

    this.timerRegressivo.removeAllEventListeners();
    this.timerRegressivo.addEventListener('secondsUpdated', () => {
      const vals = this.timerRegressivo.getTimeValues();
      // Em countdown, getTimeValues() retorna o tempo RESTANTE
      const remainingSeconds = vals.seconds + vals.minutes * 60 + vals.hours * 3600;
      callbacks.onRegressivoTick({
        formatted: vals.toString(),
        totalSeconds: remainingSeconds
      });
    });

    this.timerRegressivo.addEventListener('targetAchieved', () => {
      callbacks.onRegressivoEnd();
    });

    // stop() reseta o estado interno — start() após pause() apenas retoma, sem respeitar startValues
    this.timer.stop();
    this.timerRegressivo.stop();
    this.timer.start({ startValues: { seconds: tempoInicial } });
    this.timerRegressivo.start({
      countdown: true,
      startValues: { seconds: tempoRegressivo }
    });
  }

  /**
   * Pausa os timers de progresso e regressivo.
   * Retorna os segundos de progresso no momento da pausa.
   */
  pauseProgress(): number {
    const vals = this.timer.getTimeValues();
    const elapsed = vals.seconds + vals.minutes * 60 + vals.hours * 3600;
    this.timer.pause();
    this.timerRegressivo.pause();
    return elapsed;
  }

  /**
   * Inicia o timer de pausa a partir de um acumulado inicial.
   */
  startPause(
    tempoParadoInicial: number,
    onPausaTick: (tick: TimerTick) => void
  ): void {
    // stop() para e reseta — evita retomar posição antiga
    this.timerParado.stop();
    this.timerParado.removeAllEventListeners();
    this.timerParado.start({ startValues: { seconds: tempoParadoInicial } });
    this.timerParado.addEventListener('secondsUpdated', () => {
      const vals = this.timerParado.getTimeValues();
      onPausaTick({
        formatted: vals.toString(),
        totalSeconds: vals.seconds + vals.minutes * 60 + vals.hours * 3600
      });
    });
  }

  /**
   * Para e limpa o timer de pausa.
   * Retorna os segundos de pausa acumulados.
   */
  stopPause(): number {
    const vals = this.timerParado.getTimeValues();
    const elapsed = vals.seconds + vals.minutes * 60 + vals.hours * 3600;
    this.timerParado.stop();
    return elapsed;
  }

  /**
   * Para todos os timers.
   */
  stopAll(): void {
    // stop() reseta o estado interno do easytimer (pause apenas congela)
    this.timer.stop();
    this.timerRegressivo.stop();
    this.timerParado.stop();
    this.timer.removeAllEventListeners();
    this.timerRegressivo.removeAllEventListeners();
    this.timerParado.removeAllEventListeners();
  }

  // ─── Checagem de estado ───────────────────────────────────────────────────────

  isProgressRunning(): boolean {
    return this.timer.isRunning();
  }

  isRegressivoRunning(): boolean {
    return this.timerRegressivo.isRunning();
  }

  isPausaRunning(): boolean {
    return this.timerParado.isRunning();
  }

  isAnyRunning(): boolean {
    return this.timer.isRunning() || this.timerRegressivo.isRunning() || this.timerParado.isRunning();
  }

  // ─── Leitura de valores atuais ────────────────────────────────────────────────

  getProgressFormatted(): string {
    return this.timer.getTimeValues().toString();
  }

  getProgressSeconds(): number {
    const v = this.timer.getTimeValues();
    return v.seconds + v.minutes * 60 + v.hours * 3600;
  }

  getPausaFormatted(): string {
    return this.timerParado.getTimeValues().toString();
  }

  getPausaSeconds(): number {
    const v = this.timerParado.getTimeValues();
    return v.seconds + v.minutes * 60 + v.hours * 3600;
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────────

  /**
   * Para e limpa todos os timers.
   * DEVE ser chamado pelo componente pai no ionViewWillLeave / ngOnDestroy.
   * Nota: Angular não invoca ngOnDestroy em services — por isso é método público.
   */
  destroy(): void {
    this.stopAll();
  }
}
